package com.tovtalk.ui.screen.search

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.filled.PlayCircleOutline
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.History
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Verified
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalFocusManager
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.Post
import com.tovtalk.domain.entity.User
import com.tovtalk.domain.repository.HashtagInfo
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.search.SearchViewModel
import androidx.compose.foundation.layout.widthIn
import com.tovtalk.ui.adaptive.adaptive
import com.tovtalk.ui.adaptive.responsiveGridColumns
import com.tovtalk.ui.components.TovTalkAsyncImage
import com.tovtalk.ui.components.TovTalkAvatar
import com.tovtalk.ui.components.TovTalkSearchField
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.format.formatCount
import com.tovtalk.utils.strings.StringProvider

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SearchScreen(
    onNavigateToProfile: ((Int) -> Unit)? = null,
    onHashtagClick: ((String) -> Unit)? = null,
    onPostClick: ((Post) -> Unit)? = null,
    viewModel: SearchViewModel = koinInject()
) {
    val searchQuery by viewModel.searchQuery.collectAsState()
    val searchResults by viewModel.searchResults.collectAsState()
    val recentSearches by viewModel.recentSearches.collectAsState()
    val suggestedArtists by viewModel.suggestedArtists.collectAsState()
    val suggestedArtistsHasMore by viewModel.suggestedArtistsHasMore.collectAsState()
    val suggestedArtistsLoadingMore by viewModel.suggestedArtistsLoadingMore.collectAsState()
    val followLoadingUserId by viewModel.followLoadingUserId.collectAsState()
    val trendingHashtags by viewModel.trendingHashtags.collectAsState()
    val hashtagSuggestions by viewModel.hashtagSuggestions.collectAsState()
    val hashtagPosts by viewModel.hashtagPosts.collectAsState()
    val focusManager = LocalFocusManager.current

    val maxContentWidth = adaptive(
        compact = Dp.Unspecified,
        mediumCompact = 400.dp,
        medium = 400.dp,
        expanded = 400.dp,
        large = 400.dp,
        extraLarge = 400.dp
    )
    val gridColumns = responsiveGridColumns(
        compact = 3, mediumCompact = 3, medium = 4, expanded = 5, large = 6, extraLarge = 8
    )

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(TovTalkTheme.colors.backgroundPrimary),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .statusBarsPadding()
        ) {
            // Search bar and clear button — centered with maxContentWidth
            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.TopCenter
            ) {
                Column(
                    modifier = Modifier
                        .widthIn(max = maxContentWidth)
                        .fillMaxWidth()
                        .padding(horizontal = TovTalkTheme.spacing.md)
                ) {
                    TovTalkSearchField(
                        value = searchQuery,
                        onValueChange = { viewModel.updateSearchQuery(it) },
                        placeholder = StringProvider.get("search_placeholder"),
                        onSearch = { },
                        modifier = Modifier.fillMaxWidth()
                    )
                    AnimatedVisibility(
                        visible = searchQuery.isNotEmpty(),
                        enter = fadeIn(),
                        exit = fadeOut()
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(top = TovTalkTheme.spacing.xs),
                            horizontalArrangement = Arrangement.End
                        ) {
                            TextButton(onClick = { viewModel.clearSearch() }) {
                                Text(
                                    text = StringProvider.get("common_clear"),
                                    style = TovTalkTheme.typography.labelMedium,
                                    color = TovTalkTheme.colors.brand
                                )
                            }
                        }
                    }
                }
            }
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
            // Content area — grid is full width, list content is centered
            if (searchQuery.startsWith("#") && searchQuery.length > 1) {
                HashtagPostsGrid(
                    hashtagPosts = hashtagPosts,
                    gridColumns = gridColumns,
                    onPostClick = { post -> onPostClick?.invoke(post) },
                    onLoadMore = { viewModel.loadMoreHashtagPosts() }
                )
            } else Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = TovTalkTheme.spacing.md),
                contentAlignment = Alignment.TopCenter
            ) {
                Column(
                    modifier = Modifier
                        .widthIn(max = maxContentWidth)
                        .fillMaxSize()
                ) {
            when (val state = searchResults) {
                is UiState.Success -> {
                    val list = (state as UiState.Success<List<User>>).data
                    LazyColumn(
                        verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.xxs)
                    ) {
                        if (hashtagSuggestions.isNotEmpty()) {
                            item {
                                HashtagSuggestionsSection(
                                    suggestions = hashtagSuggestions,
                                    onHashtagClick = { tag -> viewModel.searchByHashtag(tag) }
                                )
                                Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
                            }
                        }
                        if (list.isEmpty() && hashtagSuggestions.isEmpty()) {
                            item {
                                Box(
                                    modifier = Modifier.fillMaxWidth().padding(vertical = TovTalkTheme.spacing.xl),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        text = StringProvider.get("search_no_results"),
                                        style = TovTalkTheme.typography.bodyMedium,
                                        color = TovTalkTheme.colors.textSecondary
                                    )
                                }
                            }
                        }
                        items(list, key = { it.id }) { user ->
                            ModernUserSearchItem(
                                user = user,
                                onClick = { onNavigateToProfile?.invoke(user.id) },
                                onFollowClick = { viewModel.followUser(user.id) },
                                isFollowLoading = followLoadingUserId == user.id
                            )
                        }
                    }
                }
                is UiState.Idle -> {
                    if (searchQuery.isBlank()) {
                        Column(modifier = Modifier.fillMaxSize()) {
                            if (recentSearches.isNotEmpty()) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(
                                        text = StringProvider.get("search_recent_searches"),
                                        style = TovTalkTheme.typography.titleSmall,
                                        color = TovTalkTheme.colors.textSecondary
                                    )
                                    TextButton(onClick = { viewModel.clearRecentSearches() }) {
                                        Text(
                                            text = StringProvider.get("search_clear_history"),
                                            style = TovTalkTheme.typography.labelMedium,
                                            color = TovTalkTheme.colors.brand
                                        )
                                    }
                                }
                                LazyColumn(
                                    modifier = Modifier.fillMaxWidth(),
                                    verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.none)
                                ) {
                                    items(recentSearches, key = { it }) { query ->
                                        Row(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .clickable { viewModel.selectRecentSearch(query) }
                                                .padding(vertical = TovTalkTheme.spacing.sm),
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Icon(
                                                Icons.Default.History,
                                                contentDescription = null,
                                                modifier = Modifier.size(TovTalkTheme.sizes.iconSm),
                                                tint = TovTalkTheme.colors.textTertiary
                                            )
                                            Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
                                            Text(
                                                text = query,
                                                style = TovTalkTheme.typography.bodyMedium,
                                                color = TovTalkTheme.colors.textPrimary
                                            )
                                        }
                                    }
                                }
                                Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
                            }
                            TrendingHashtagsSection(
                                trendingState = trendingHashtags,
                                onHashtagClick = { tag -> viewModel.searchByHashtag(tag) }
                            )
                            Text(
                                text = StringProvider.get("search_suggested_artists"),
                                style = TovTalkTheme.typography.titleSmall,
                                color = TovTalkTheme.colors.textSecondary,
                                modifier = Modifier.padding(bottom = TovTalkTheme.spacing.sm)
                            )
                            when (val suggested = suggestedArtists) {
                                is UiState.Loading -> {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(120.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        CircularProgressIndicator(color = TovTalkTheme.colors.brand)
                                    }
                                }
                                is UiState.Success -> {
                                    val list = suggested.data
                                    if (list.isEmpty()) {
                                        Box(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .height(100.dp),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            Text(
                                                text = StringProvider.get("search_find_people"),
                                                style = TovTalkTheme.typography.bodySmall,
                                                color = TovTalkTheme.colors.textTertiary
                                            )
                                        }
                                    } else {
                                        val listState = rememberLazyListState()
                                        LaunchedEffect(listState.firstVisibleItemIndex) {
                                            focusManager.clearFocus()
                                        }
                                        val shouldLoadMoreArtists by remember {
                                            derivedStateOf {
                                                val lastVisibleIndex = listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0
                                                list.isNotEmpty() && lastVisibleIndex >= list.size - 2 &&
                                                    suggestedArtistsHasMore && !suggestedArtistsLoadingMore
                                            }
                                        }
                                        LaunchedEffect(shouldLoadMoreArtists) {
                                            if (shouldLoadMoreArtists) viewModel.loadMoreSuggestedArtists()
                                        }
                                        Column(modifier = Modifier.weight(1f)) {
                                            LazyColumn(
                                                state = listState,
                                                modifier = Modifier.fillMaxSize(),
                                                verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.xxs)
                                            ) {
                                                items(list, key = { it.id }) { user ->
                                                    ModernUserSearchItem(
                                                        user = user,
                                                        onClick = { onNavigateToProfile?.invoke(user.id) },
                                                        onFollowClick = { viewModel.followUser(user.id) },
                                                        isFollowLoading = followLoadingUserId == user.id
                                                    )
                                                }
                                                if (suggestedArtistsLoadingMore) {
                                                    item {
                                                        Box(
                                                            modifier = Modifier
                                                                .fillMaxWidth()
                                                                .padding(TovTalkTheme.spacing.md),
                                                            contentAlignment = Alignment.Center
                                                        ) {
                                                            CircularProgressIndicator(
                                                                modifier = Modifier.size(24.dp),
                                                                color = TovTalkTheme.colors.brand
                                                            )
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                else -> {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .height(100.dp),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text(
                                            text = StringProvider.get("search_find_people"),
                                            style = TovTalkTheme.typography.bodySmall,
                                            color = TovTalkTheme.colors.textTertiary
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
                is UiState.Loading -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = TovTalkTheme.colors.brand)
                    }
                }
                is UiState.Error -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                text = state.message,
                                style = TovTalkTheme.typography.bodyMedium,
                                color = TovTalkTheme.colors.error
                            )
                        }
                    }
                }
                is UiState.KashrutBlocked -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = state.message,
                            style = TovTalkTheme.typography.bodyMedium,
                            color = TovTalkTheme.colors.textSecondary
                        )
                    }
                }
            }
                } // end Column(widthIn)
            } // end Box(contentAlignment)
        }
    }
}

@Composable
private fun TrendingHashtagsSection(
    trendingState: UiState<List<HashtagInfo>>,
    onHashtagClick: (String) -> Unit,
) {
    when (trendingState) {
        is UiState.Success -> {
            Text(
                text = "האשטגים טרנדיים",
                style = TovTalkTheme.typography.titleSmall,
                color = TovTalkTheme.colors.textSecondary,
                modifier = Modifier.padding(bottom = TovTalkTheme.spacing.sm)
            )
            trendingState.data.forEach { hashtag ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onHashtagClick(hashtag.hashtag) }
                        .padding(vertical = TovTalkTheme.spacing.sm),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "#${hashtag.hashtag}",
                            style = TovTalkTheme.typography.bodyMedium,
                            color = TovTalkTheme.colors.textPrimary
                        )
                        Text(
                            text = "${formatCount(hashtag.postCount.toLong())} פוסטים",
                            style = TovTalkTheme.typography.caption,
                            color = TovTalkTheme.colors.textTertiary
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
        }
        else -> {}
    }
}

@Composable
private fun HashtagSuggestionsSection(
    suggestions: List<HashtagInfo>,
    onHashtagClick: (String) -> Unit,
) {
    suggestions.forEach { hashtag ->
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable { onHashtagClick(hashtag.hashtag) }
                .padding(vertical = TovTalkTheme.spacing.sm),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = "#${hashtag.hashtag}",
                    style = TovTalkTheme.typography.bodyMedium,
                    color = TovTalkTheme.colors.textPrimary
                )
                Text(
                    text = "${formatCount(hashtag.postCount.toLong())} פוסטים",
                    style = TovTalkTheme.typography.caption,
                    color = TovTalkTheme.colors.textTertiary
                )
            }
        }
    }
}

@Composable
private fun ModernUserSearchItem(
    user: User,
    onClick: () -> Unit,
    onFollowClick: (() -> Unit)? = null,
    isFollowLoading: Boolean = false
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = TovTalkTheme.spacing.sm),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TovTalkAvatar(
            imageUrl = user.profilePhoto,
            size = TovTalkTheme.sizes.avatarLg
        )
        Spacer(modifier = Modifier.width(TovTalkTheme.spacing.sm))
        Column(modifier = Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = user.fullname.ifBlank { user.username },
                    style = TovTalkTheme.typography.titleSmall,
                    color = TovTalkTheme.colors.textPrimary
                )
                if (user.isVerified) {
                    Spacer(modifier = Modifier.width(TovTalkTheme.spacing.xxs))
                    Icon(
                        Icons.Default.Verified,
                        contentDescription = StringProvider.get("search_verified"),
                        modifier = Modifier.size(TovTalkTheme.sizes.iconXs),
                        tint = TovTalkTheme.colors.brand
                    )
                }
            }
            Text(
                text = user.username,
                style = TovTalkTheme.typography.bodySmall,
                color = TovTalkTheme.colors.textSecondary
            )
            if (user.followerCount > 0) {
                Text(
                    text = StringProvider.get("search_followers_count", formatCount(user.followerCount.toLong())),
                    style = TovTalkTheme.typography.caption,
                    color = TovTalkTheme.colors.textTertiary
                )
            }
        }
        if (user.isFollowing) {
            Text(
                text = StringProvider.get("profile_following"),
                style = TovTalkTheme.typography.labelMedium,
                color = TovTalkTheme.colors.textTertiary
            )
        } else if (onFollowClick != null) {
            Button(
                onClick = onFollowClick,
                enabled = !isFollowLoading,
                modifier = Modifier.size(80.dp, 32.dp)
            ) {
                if (isFollowLoading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(16.dp),
                        color = TovTalkTheme.colors.onBrand,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text(
                        text = StringProvider.get("profile_follow"),
                        style = TovTalkTheme.typography.labelMedium
                    )
                }
            }
        }
    }
}

@Composable
private fun SuggestedArtistItem(
    user: User,
    onProfileClick: () -> Unit,
    onFollowClick: () -> Unit,
    isFollowLoading: Boolean
) {
    Column(
        modifier = Modifier
            .width(120.dp)
            .clickable(onClick = onProfileClick),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        TovTalkAvatar(
            imageUrl = user.profilePhoto,
            size = TovTalkTheme.sizes.avatarLg
        )
        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xs))
        Text(
            text = user.fullname.ifBlank { user.username },
            style = TovTalkTheme.typography.labelMedium,
            color = TovTalkTheme.colors.textPrimary,
            maxLines = 1
        )
        if (user.followerCount > 0) {
            Text(
                text = formatCount(user.followerCount.toLong()) + " " + StringProvider.get("profile_followers"),
                style = TovTalkTheme.typography.caption,
                color = TovTalkTheme.colors.textTertiary
            )
        }
        if (user.isFollowing) {
            Text(
                text = StringProvider.get("profile_following"),
                style = TovTalkTheme.typography.caption,
                color = TovTalkTheme.colors.textTertiary
            )
        } else {
            Button(
                onClick = onFollowClick,
                enabled = !isFollowLoading,
                modifier = Modifier.padding(top = TovTalkTheme.spacing.xxs)
            ) {
                if (isFollowLoading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(14.dp),
                        color = TovTalkTheme.colors.onBrand,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text(text = StringProvider.get("profile_follow"), style = TovTalkTheme.typography.labelSmall)
                }
            }
        }
    }
}

@Composable
private fun HashtagPostsGrid(
    hashtagPosts: UiState<List<Post>>,
    gridColumns: Int,
    onPostClick: (Post) -> Unit,
    onLoadMore: () -> Unit,
) {
    when (hashtagPosts) {
        is UiState.Loading -> {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
        }
        is UiState.Error -> {
            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Text(
                    text = hashtagPosts.message,
                    style = TovTalkTheme.typography.bodyMedium,
                    color = TovTalkTheme.colors.textSecondary
                )
            }
        }
        is UiState.Success -> {
            val posts = hashtagPosts.data
            if (posts.isEmpty()) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text(
                        text = "אין תוצאות",
                        style = TovTalkTheme.typography.bodyMedium,
                        color = TovTalkTheme.colors.textSecondary
                    )
                }
            } else {
                val gridState = rememberLazyGridState()
                val shouldLoadMore by remember {
                    derivedStateOf {
                        val lastVisible = gridState.layoutInfo.visibleItemsInfo.lastOrNull()
                        lastVisible != null &&
                            lastVisible.index >= gridState.layoutInfo.totalItemsCount - 6
                    }
                }
                LaunchedEffect(shouldLoadMore) {
                    if (shouldLoadMore) onLoadMore()
                }
                LazyVerticalGrid(
                    columns = GridCells.Fixed(gridColumns),
                    state = gridState,
                    modifier = Modifier.fillMaxSize(),
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                    verticalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    items(posts, key = { it.id }) { post ->
                        HashtagPostItem(
                            post = post,
                            onClick = { onPostClick(post) }
                        )
                    }
                }
            }
        }
        else -> {}
    }
}

@Composable
private fun HashtagPostItem(
    post: Post,
    onClick: () -> Unit,
) {
    Box(
        modifier = Modifier
            .aspectRatio(9f / 16f)
            .clip(RoundedCornerShape(8.dp))
            .background(TovTalkTheme.colors.backgroundSecondary)
            .clickable(onClick = onClick),
    ) {
        val thumbnailUrl = post.thumbnail
        if (!thumbnailUrl.isNullOrEmpty()) {
            TovTalkAsyncImage(
                url = thumbnailUrl,
                modifier = Modifier.fillMaxSize(),
                clipToCircle = false,
                contentScale = ContentScale.Crop,
            )
        } else {
            Icon(
                Icons.Default.PlayCircleOutline,
                contentDescription = null,
                modifier = Modifier.size(48.dp).align(Alignment.Center),
                tint = TovTalkTheme.colors.textTertiary
            )
        }
        post.author.let { author ->
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.BottomStart)
                    .background(TovTalkTheme.colors.backgroundPrimary.copy(alpha = 0.6f))
                    .padding(horizontal = 6.dp, vertical = 4.dp)
            ) {
                Text(
                    text = "@${author.username}",
                    style = TovTalkTheme.typography.labelSmall,
                    color = TovTalkTheme.colors.textPrimary,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}
